import { parseISO, startOfWeek, endOfWeek, format } from "date-fns";
import { CalendarSlot } from "../types";
import { PSY_SERVICE, NFB_SERVICE } from "../../../utils/constants";
import { ClientRow, SessionInfo, WeekRange } from "./listTypes";
import { buildPsyClientIdSet, createEmptyClientRow, updateClientRowWithSlot } from "./listHelpers";

export const calculateWeeksWithMismatch = (
    sessionsByDate: Record<string, SessionInfo>,
    reservationCount: number,
    isWeekly: boolean
): Array<WeekRange> => {
    if (!isWeekly) return [];
    if (reservationCount === 0) return [];

    const sessionDates = Object.keys(sessionsByDate);
    if (sessionDates.length === 0) return [];

    const weekSessionCounts: Record<string, { count: number; weekStart: Date; weekEnd: Date }> = {};

    sessionDates.forEach((dateStr) => {
        const date = parseISO(dateStr);
        const weekStart = startOfWeek(date, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
        const weekKey = format(weekStart, "yyyy-MM-dd");

        if (!weekSessionCounts[weekKey]) {
            weekSessionCounts[weekKey] = { count: 0, weekStart, weekEnd };
        }
        weekSessionCounts[weekKey].count += 1;
    });

    const sortedWeeks = Object.entries(weekSessionCounts)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

    const weeksToCheck = sortedWeeks.slice(0, -1);

    const mismatchedWeeks: Array<WeekRange> = [];

    weeksToCheck.forEach(([, weekData]) => {
        const sessionDiff = weekData.count - reservationCount;

        if (sessionDiff !== 0) {
            mismatchedWeeks.push({
                start: format(weekData.weekStart, "dd/MM"),
                end: format(weekData.weekEnd, "dd/MM"),
                diff: sessionDiff,
            });
        }
    });

    return mismatchedWeeks;
};

export const processClientRows = (allSlots: Array<CalendarSlot>, hasTherapistFilter: boolean): Array<ClientRow> => {
    const psyClientIds = buildPsyClientIdSet(allSlots);
    const clientsMap: Record<string, ClientRow> = {};

    allSlots.forEach((slot) => {
        const serviceId = slot.serviceId ?? PSY_SERVICE;
        const isNfb = serviceId === NFB_SERVICE;

        if (isNfb && hasTherapistFilter && !psyClientIds.has(slot.clientId)) {
            return;
        }

        const key = `${slot.clientId}_${slot.treatmentId}`;

        if (!clientsMap[key]) {
            clientsMap[key] = createEmptyClientRow(slot);
        }

        updateClientRowWithSlot(clientsMap[key], slot, isNfb);
    });

    const rows = Object.values(clientsMap);
    rows.sort((a, b) => a.clientId.localeCompare(b.clientId));
    return rows;
};
